import axios from 'axios';
import { camelCase, debounce, flatten, reduce, replace } from 'lodash';
import { DateTime } from 'luxon';
import { isHttpsUri } from 'valid-url';
import { onMounted, onUnmounted, ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import { OPERATORS } from '@/constants/populations';
import { COPILOT_BASE_PATH } from '@/constants/router';
import { STORAGE_KEYS_TO_CLEAR_ON_ORG_SWITCH } from '@/constants/storageKeys';
import { captureException } from '@/errors';

export const isLoginPopup = () => sessionStorage.getItem('loginPopup') === '1';

export const PREVIEW_BUILD_REGEX = /^https:\/\/mr-\d+\.app\.cb\.c7m\.io$/;

export const LOGIN_POPUP_PARAM = 'isLoginPopup';
export const MINIMAL_VIEW_PARAM = 'minimal';
export const SSO_PARAM = 'sso';

const isLoginPopupQueryStr = `${LOGIN_POPUP_PARAM}=1`;

export const setIsLoginPopup = () => {
  if (
    window.location.href.includes(isLoginPopupQueryStr) &&
    !window.location.href.includes(`${COPILOT_BASE_PATH}/login`)
  ) {
    sessionStorage.setItem('loginPopup', '1');
  }
};

/* Whether the user's operating system meta key is Ctrl or Meta */
export const ctrlOrMeta = () => (operatingSystem() === 'macOS' ? '⌘' : 'ctrl');

/* Helper for ctrlOrMeta */
const operatingSystem = () => {
  const appVersion = navigator.appVersion.toLowerCase();
  if (appVersion.indexOf('win') > -1) {
    return 'Windows';
  } else if (appVersion.indexOf('mac') > -1) {
    return 'macOS';
  }
  return '*nix';
};

/* Search at the top-level of an object for a case-insensitive string key */
export const shallowObjectSearch = (obj, keys, search) => {
  for (const key of keys) {
    if (typeof obj[key] === 'string') {
      if (obj[key]?.toLowerCase().includes(search.toLowerCase())) return true;
    }
  }
  return false;
};

/* Whether the element is in the visible viewport */
export const isElementInViewport = (element) => {
  const bounding = element.getBoundingClientRect();
  const result =
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.right <=
      (window.innerWidth || document.documentElement.clientWidth) &&
    bounding.bottom <=
      (window.innerHeight || document.documentElement.clientHeight);
  return result;
};

/* Truthy or zero, used to check for truthiness when zero is possible */
export const isTruthyOrZero = (number) => {
  return number || number === 0;
};

/* Generates UUID4 string */
export const uuid4 = () => {
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return template.replace(/[xy]/g, (c) => {
    /* eslint-disable-next-line custom/no-bitwise-operators */
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line custom/no-bitwise-operators
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/* Gets all URL parameters by a given key */
export const getUrlParams = (paramKey) => {
  const params = new URLSearchParams(location.search);
  return params.getAll(paramKey);
};

/* Opens Itercom with given message if enabled, otherwise opens email client */
export const intercomOrEmailMessage = (to, subject, body) => {
  if (window.Intercom) {
    window.Intercom('showNewMessage', body);
  } else {
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    location.href = `mailto:${to}?subject=${encodedSubject}&body=${encodedBody}`;
  }
};

export const copyToClipBoard = async (textToCopy) => {
  if (!navigator.clipboard) {
    copyTextFallback(textToCopy);
    return;
  }
  try {
    await navigator.clipboard.writeText(textToCopy);
  } catch (err) {
    captureException(err);
  }
};

/* Helper for copyToClipBoard */
const copyTextFallback = (textToCopy) => {
  const textArea = document.createElement('textarea');
  textArea.value = textToCopy;

  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand('copy');
  } catch (err) {
    captureException(err);
  }
  document.body.removeChild(textArea);
};

/* Validity of origins for oAuth applications */
export const areAllOriginsValid = (origins) => {
  return origins.every((origin) => isOriginValid(origin));
};
export const areAllCallbacksValid = (callbacks) => {
  return callbacks.every((callback) => isCallbackValid(callback));
};

/* Helpers for validity checks */
const isOriginValid = (tag) => {
  return isUrlValid(tag, ['chrome-extensions://']);
};
const isCallbackValid = (tag) => {
  return isUrlValid(tag, []);
};

export const isExternalUrl = (url) => {
  if (!url) return false;
  try {
    const externalUrl = new URL(url);
    if (externalUrl.href) return true;
  } catch (_err) {
    /* Will throw if not a valid URL */
    return false;
  }
};

/* Checks whether URL is https or matches allowed protocol list */
export const isUrlValid = (tag, allowedProtocols = []) => {
  let matchesAllowedProtocols = false;
  allowedProtocols.forEach((allowedProtocol) => {
    if (tag.startsWith(allowedProtocol)) {
      matchesAllowedProtocols = true;
    }
  });
  if (tag.startsWith('https://')) {
    return true;
  } else if (matchesAllowedProtocols) {
    return true;
  }
  return isHttpsUri(replace(tag, '*', 'x'));
};

/* Gets mdm field by property type off item, or returns null */
export const getMdmField = (item, mdmPropertyName, isOursFilter = null) => {
  if (!item?.fields) return null;
  const field = item.fields.find((field) => {
    // filter for mdm property matching
    if (field.mdm_property !== mdmPropertyName) return false;
    // is_ours can either be true or false. if we care about that value, then supply the optional isOursFilter
    if (isOursFilter !== null) return field.is_ours === isOursFilter;
    return true;
  });
  return field ? field.value : null;
};

/* Routes to next route, if it exists */
export const resolveNext = (router, next) => {
  if (next === null || typeof next === 'string') {
    return next;
  }
  return router.resolve(next);
};

export const switchOrganization = (
  router,
  organizationId,
  nextRoute = { name: 'main' },
) => {
  const destination = router.resolve(nextRoute);
  const baseUrl = `${window.location.origin}${destination.path}`;
  const queryParams = new URLSearchParams({ organization_id: organizationId });

  STORAGE_KEYS_TO_CLEAR_ON_ORG_SWITCH.forEach(({ key, storage }) =>
    storage.removeItem(key),
  );

  const newUrl = `${baseUrl}?${queryParams.toString()}`;
  window.location.href = newUrl;
};

export const formatNumberWithCommas = (num) => {
  if (num === null || num === undefined) return num;
  return new Intl.NumberFormat('en-US').format(num);
};

export const populationProcessedAtText = (str) => {
  return str ? DateTime.fromISO(str).toRelative() : '';
};

export const returnTrimmedDomain = (domain) => {
  if (!domain) return '';
  return domain.replace(/^https?:\/\//, '');
};

export const transformedFilterParts = (filterParts, fieldsToRemove = []) => {
  return filterParts.map((filterPart) => {
    const updatedFilterPart = fieldsToRemove.reduce((acc, field) => {
      const { [field]: _, ...rest } = acc;
      return rest;
    }, filterPart);

    const matchingOperator = OPERATORS.find(
      (operator) => operator.id === updatedFilterPart.operator,
    );
    if (!matchingOperator || !matchingOperator.filterPartOverrides) {
      return updatedFilterPart;
    }
    return {
      ...updatedFilterPart,
      ...matchingOperator.filterPartOverrides,
    };
  });
};

export const toCamelCase = (obj) => {
  const transformed = {};
  Object.keys(obj).forEach((key) => {
    if (!obj[key]) {
      transformed[camelCase(key)] = obj[key];
    } else if (typeof obj[key] === 'object') {
      transformed[camelCase(key)] = toCamelCase(obj[key]);
    } else {
      transformed[camelCase(key)] = obj[key];
    }
  });

  return transformed;
};

export const indexBy = (arr, key) => {
  return reduce(
    arr,
    (acc, el) => {
      acc[el[key]] = el;
      return acc;
    },
    {},
  );
};

export const jsonParseSafe = (s) => {
  try {
    return JSON.parse(s);
  } catch (_SyntaxError) {
    return null;
  }
};

export const REGEX_URL =
  /^[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?$/i;

export const trim = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  return value.trim();
};

export const isEmailValid = (str) => {
  const VALID_EMAIL = /\S+@\S+\.\S+/;
  return VALID_EMAIL.test(str.toLowerCase());
};

export const popupCenter = ({ url, title, w, h, win }) => {
  const y = win.top.outerHeight / 2 + win.top.screenY - h / 2;
  const x = win.top.outerWidth / 2 + win.top.screenX - w / 2;
  return win.open(
    url,
    title,
    `scrollbars=yes,
    width=${w},
    height=${h},
    top=${y},
    left=${x}`,
  );
};

export function createAxiosWithRetry() {
  const userAxios = axios.create();

  userAxios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      try {
        return axios.request(error.config);
      } catch (_err) {
        return Promise.reject(error);
      }
    },
  );

  return userAxios;
}

export function createCrossbeamApiWithRetry() {
  const apiWithRetry = new Proxy(crossbeamApi, {
    get(target, prop, receiver) {
      const originalValue = Reflect.get(target, prop, receiver);

      if (!originalValue || typeof originalValue !== 'function') {
        return originalValue;
      }

      if (['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].includes(prop.toString())) {
        return async function (...args) {
          try {
            const response = await originalValue.apply(target, args);
            if (!response) throw new Error('No response received');
            if (![null, undefined].includes(response.error))
              throw response.error;
            return response;
          } catch (error) {
            try {
              return await originalValue.apply(target, args);
            } catch (_retryError) {
              return Promise.reject(error);
            }
          }
        };
      }

      return originalValue;
    },
  });

  return apiWithRetry;
}

export const hubSpotFormApi = async (formId, args, path, envID) => {
  const url = `https://api.hsforms.com/submissions/v3/integration/submit/${envID}/${formId}`;
  const mapped = [];
  for (const key in args) {
    const fields = {
      objectTypeId: '0-1',
      name: key,
      value: args[key],
    };
    mapped.push(fields);
  }
  const payload = {
    fields: mapped,
    context: {
      pageUri: `www.crossbeam.com/${path}`,
      pageName: path,
    },
    legalConsentOptions: {
      consent: {
        consentToProcess: true,
        text: 'I agree to allow Crossbeam to store and process my personal data.',
      },
    },
  };
  await axios.post(url, payload, {
    withCredentials: false,
  });
};

export const fullPluralize = (numberOrArray, textSingle, textPlural) => {
  const len = Array.isArray(numberOrArray)
    ? numberOrArray.length
    : numberOrArray;
  if (len === 0) {
    return `No ${textPlural}`;
  } else if (len === 1) {
    return `1 ${textSingle}`;
  }
  return `${len} ${textPlural}`;
};

export const simplePluralize = (items, text) => {
  return fullPluralize(items, text, `${text}s`);
};

export const extractDataShareIds = (elements) => {
  return flatten(
    Object.keys(elements).map((key) => {
      return 'items' in elements[key]
        ? flatten(elements[key].items.map((item) => item.data_share_ids))
        : elements[key].data_share_ids;
    }),
  );
};

export const configureGlobalSettings = (csrfToken) => {
  axios.defaults.headers.common.Accept = 'application/json';
  axios.defaults.headers.post['X-CSRFToken'] = csrfToken;
  axios.defaults.headers.put['X-CSRFToken'] = csrfToken;
  axios.defaults.headers.delete['X-CSRFToken'] = csrfToken;
  axios.defaults.headers.patch['X-CSRFToken'] = csrfToken;
};

export const centsToDollars = (num) => {
  const CENTS_IN_CURRENCY = 100;
  const inDollars = `$${formatNumberWithCommas(num / CENTS_IN_CURRENCY)}`;
  if (!inDollars.includes('.')) return inDollars;

  /* Append an extra zero if the result is 150.2, 100.1, etc. */
  const [dollars, cents] = inDollars.split('.');
  return `${dollars}.${cents.length === 1 ? `${cents}0` : cents}`;
};

export const extractLocalDate = (dateTimeString) =>
  DateTime.fromISO(dateTimeString).toLocaleString({
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

/* Promise that resolves once the HTML node is rendered in the DOM */
export function waitForElement(selector) {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}

export const watchForResize = (selector, changeHandler) => {
  let resizeObserver;
  onMounted(() => {
    const element = document.querySelector(selector);
    resizeObserver = new ResizeObserver((entries) =>
      changeHandler({
        height: entries[0].target.clientHeight,
        width: entries[0].target.clientWidth,
        scrollHeight: entries[0].target.scrollHeight,
        scrollWidth: entries[0].target.scrollWidth,
        element: entries[0].target,
      }),
    );
    resizeObserver.observe(element);
  });

  onUnmounted(() => resizeObserver.disconnect());
};

/* mainly used to quickly emulate a slow XHR */
export const slowResponse = (delayedPromise, delay) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      delayedPromise.then((resp) => {
        resolve(resp);
      });
    }, delay);
  });
};

export const currencyFormatter = (value, includeCents = false) => {
  if (!value) return '$--';

  let formattedValue = value;

  // If value has cents, round to the nearest dollar
  if (!includeCents) {
    const hasCents = formattedValue % 1 !== 0;
    if (hasCents) formattedValue = Math.round(formattedValue);
  }

  const options = {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: includeCents ? 2 : 0,
  };

  const usd = new Intl.NumberFormat('en-US', options);
  return usd.format(formattedValue);
};

/**
 * Returns a sorting function for a list of objects. Sorts by their key in either ascending or descending order.
 * @param {string} [field] The field to sort by, e.g. name, date etc
 * @param {string} [order] The order of the sort, either ascending or descending
 * @param {string} [type] The type of the sort, e.g. string; raw (numbers, luxon DateTimes); timestamp (ISO); or length for arrays
 * @returns {Function} The function to sort the list
 */

const notThere = new Set([null, undefined]);
export function sortByKey(field = 'name', order = 'desc', type = 'string') {
  const defaultSort = 0;
  function notThereCheck(val1, val2) {
    if (notThere.has(val1) && notThere.has(val2)) return defaultSort;
    if (notThere.has(val1) && !notThere.has(val2))
      return order === 'desc' ? 1 : -1;
    if (!notThere.has(val1) && notThere.has(val2))
      return order === 'desc' ? -1 : 1;
    return null;
  }

  return function (a, b) {
    const val1 = a[field];
    const val2 = b[field];
    const notThereRes = notThereCheck(val1, val2);
    if (notThereRes !== null) return notThereRes;
    if (type === 'string') {
      if (order === 'desc') return val1.localeCompare(val2);
      return val2.localeCompare(val1);
    }

    if (type === 'raw') {
      if (val1 === val2) return defaultSort;
      if (order === 'desc') return val1 > val2 ? -1 : 1;
      return val1 > val2 ? 1 : -1;
    }

    if (type === 'timestamp') {
      const result = new Date(val1) - new Date(val2);
      if (result === 0) return defaultSort;
      if (order === 'desc') return result > 0 ? -1 : 1;
      return result > 0 ? 1 : -1;
    }

    if (type === 'length') {
      if (val1.length === val2.length) return defaultSort;
      if (order === 'desc') return val1.length > val2.length ? -1 : 1;
      return val1.length > val2.length ? 1 : -1;
    }

    return defaultSort;
  };
}

/**
 * Used to dynamically inject strings using v-html. Only use with input from Crossbeam, not w/ outside data
 * @param {string} str
 * @param {{ values: unknown[], bold?: boolean, italic?: boolean, underline?: boolean }} [options]
 */
export function injectStrings(
  str,
  { values, bold = false, italic = false, underline = false } = {},
) {
  for (const injection of values) {
    let wrappedInjection = injection;
    if (bold) wrappedInjection = `<b>${wrappedInjection}</b>`;
    if (italic) wrappedInjection = `<i>${wrappedInjection}</i>`;
    if (underline)
      wrappedInjection = `<span class="underline decoration-dashed decoration-neutral-text-placeholder underline-offset-4">${wrappedInjection}</span>`;
    str = str.replace('%s', wrappedInjection);
  }
  return str;
}

/* sleep blocks execution for the number of seconds provided. Useful for testing slow APIs */
export async function sleep(ms = 3000) {
  await new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

/* Makes a comma separated readable list out of the values presented */
export function formatList(items, { bold, italic, underline } = {}) {
  if (items.length === 1) {
    return injectStrings('%s', { values: items, bold, italic, underline });
  }

  const firstItems = items.slice(0, -1);
  return injectStrings(`${firstItems.map(() => '%s').join(', ')} and %s`, {
    values: items,
    bold,
    italic,
    underline,
  });
}

/** Ensures that every key in an object contains a default value if it was not defined
 * @param {obj} [object] The object that may be missing values
 * @param {fallbackMap} [object] The default object, which contains fallbacks for every required field
 */
export function ensureDefaultsExist(obj, fallbackMap) {
  const res = { ...obj };
  for (const [key, value] of Object.entries(fallbackMap)) {
    if (res[key] === undefined) res[key] = value;
  }
  return res;
}

/**
 * Creates a debounced validation handler for a reactive input.
 * @param input - The ref containing the reactive input value. Type: Ref<string>
 * @param validate - The validation function (returns `true` if valid). Type: (value: string) => boolean
 * @param delay - The debounce delay in milliseconds. Type: number
 * @returns A ref that tracks the invalid state of the input. Type: Ref<boolean>
 */
export function useConditionalDebouncedValidation(input, validate, delay) {
  const isValid = ref(true);
  let lastValidState = true; // Tracks whether the previous input was valid or not

  // Instant validation function (if we want to validate immediately)
  const validateNow = (value) => {
    isValid.value = validate(value);
    lastValidState = isValid.value; // Update the previous valid state
  };

  // Debounced validation function (with delay)
  const validateWithDelay = debounce((value) => {
    isValid.value = validate(value);
    lastValidState = isValid.value; // Update the previous valid state
  }, delay);

  // Watch the input for changes
  watch(input, (newValue) => {
    const wasValid = lastValidState; // Use the last valid state to decide

    if (wasValid) {
      validateWithDelay(newValue); // Debounce validation if previously valid
    } else {
      validateNow(newValue); // Instantly validate if previously invalid
    }
  });

  return isValid;
}

/* Returns a list of fake deal navigator data */
export const setupFakeDealNavData = (partners) => {
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = array[i];
      array[i] = array[j];
      array[j] = temp;
    }
  };

  const companies = [
    { name: 'Dunder Mifflin Paper Company', domain: 'dundermifflin.com' },
    { name: 'Acme Corporation', domain: 'acmecorp.com' },
    { name: 'Globodyne Industries', domain: 'globodyne.com' },
    { name: 'Vandelay Industries', domain: 'vandelayindustries.com' },
    { name: 'Initech Corporation', domain: 'initech.com' },
    { name: 'Umbrella Corporation', domain: 'umbrellacorp.com' },
    { name: 'Wayne Enterprises', domain: 'wayneenterprises.com' },
    { name: 'Stark Industries', domain: 'starkindustries.com' },
    { name: 'Bluth Company', domain: 'bluthcompany.com' },
    { name: 'Prestige Worldwide', domain: 'prestigeworldwide.com' },
    { name: 'Hooli Technologies', domain: 'hooli.com' },
    { name: 'Pied Piper', domain: 'piedpiper.com' },
    { name: 'Massive Dynamic', domain: 'massivedynamic.com' },
    { name: 'Sterling Cooper Draper Pryce', domain: 'scdp.com' },
    { name: 'Los Pollos Hermanos', domain: 'lospolloshermanos.com' },
  ];
  shuffleArray(companies);

  const owners = [
    'Emily Rodriguez',
    'Michael Chen',
    'Sarah Thompson',
    'David Kim',
    'Jessica Martinez',
    'John Doe',
    'Jane Smith',
    'Mike Johnson',
    'Lisa Brown',
    'Tom Wilson',
  ];
  shuffleArray(owners);

  const stages = [
    'Prospecting',
    'Qualification',
    'Proposal',
    'Perception Analysis',
    'Needs Analysis',
    'Negotiation',
    'Closed Won',
    'Closed Lost',
  ];
  shuffleArray(stages);

  const fakeData = [];
  for (let i = 0; i < 15; i++) {
    const randomCompany = companies[i % companies.length];
    const randomOwner = owners[i % owners.length];
    const randomStage = stages[i % stages.length];

    // Random amount between 50,000 and 500,000
    const randomAmount = Math.floor(Math.random() * (500000 - 50000)) + 50000;

    // Random date within two months from/before today
    const closeDate = DateTime.now()
      .minus({ months: 2 })
      .plus({ days: Math.floor(Math.random() * (30 * 4)) })
      .toISO();

    const randomPartner =
      partners.length > 0
        ? partners[Math.floor(Math.random() * partners.length)]
        : null;

    fakeData.push({
      account_country: null,
      account_domain: randomCompany?.domain ?? 'real-domain.com',
      account_employees: Math.floor(Math.random() * 1000) + 50,
      account_industry: null,
      account_name: randomCompany?.name ?? 'Real Account Name',
      crossbeam_record_id: `RealRecordId${i}`,
      deal_amount_in_dollars: randomAmount,
      deal_amount_in_usd: randomAmount,
      deal_amount: randomAmount ?? 0,
      deal_close_date: closeDate,
      deal_currency: 'USD',
      deal_id: `RealDealId${i}`,
      deal_name: `${randomCompany?.name} Deal`,
      deal_owner_id: `RealOwnerId${i}`,
      deal_owner_name: randomOwner ?? 'Real Owner Name',
      deal_source_id: 0,
      deal_stage: randomStage ?? 'Prospecting',
      deal_url: null,
      important_contacts: [],
      is_customer_of: randomPartner
        ? [
            {
              partner_org_uuid: randomPartner.uuid,
              is_recommended: Math.random() > 0.5,
              key_contact_insights: [],
              insights: [],
            },
          ]
        : [],
      is_priority: false,
      last_partner_customer_date: null,
      master_source_id: 0,
      number_of_important_contacts: 0,
      recommended_partner_count: 0,
      record_id: `RealRecordId${i}`,
      total_is_customer_of_partners: 0,
      total_recommended_is_customer_of_partners: 0,
    });
  }

  shuffleArray(fakeData);
  return fakeData;
};

/**
 * Sorts an array of objects by their mdm_type property according to a predefined order
 * @param {Array} objects - Array of objects to sort
 * @returns {Array} Sorted array of objects
 */
export const sortObjectsByMdmType = (objects) => {
  const objectOrder = [
    'account',
    'deal',
    'user',
    'contact',
    'lead',
    'deal_contact_role',
    'product',
    'deal_product',
    'account_product',
  ];

  const getOrder = (mdmType) => {
    const index = objectOrder.indexOf(mdmType);
    return index === -1 ? 99 : index;
  };

  return Array.from(objects).sort(
    (a, b) => getOrder(a.mdm_type) - getOrder(b.mdm_type),
  );
};
